import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  normalizeImageSource,
  useProgressiveImage,
} from './common/useProgressiveImage';

/**
 * MapCoordinateCanvas
 * A scalable, self-sizing image canvas that:
 * - Normalizes clicks to image-relative coords (0..1)
 * - Renders pins/lines aligned to the actual displayed image (letterboxed within container)
 * - Scales cleanly at any size (e.g., modal fullscreen vs. small card)
 *
 * ``imageUrl`` accepts either:
 *   - a plain string (legacy)  — used as the full-res tier
 *   - an object ``{ thumb, preview, full, width?, height? }`` produced by
 *     the backend's ``multi_res_urls`` helper
 *
 * When a multi-res bundle is supplied, the canvas renders the best
 * currently-decoded tier and cross-blurs to sharper tiers as they
 * become available. Coordinate math stays stable across the swap
 * because every tier preserves the source aspect ratio.
 */
const MapCoordinateCanvas = ({
  imageUrl,
  className = '',
  style = {},
  aspectRatio = '16 / 9',
  onSelect, // (norm {x,y}) => void
  guess,    // {x,y} normalized (deprecated in multi-guess mode)
  guesses = [], // [{x,y, username?, color?}]
  answer,   // {x,y} normalized
  showLine = false,
  renderExtra = null, // (metrics) => ReactNode
  highlightKey = null, // username or id used to bring a guess to front
  markerStyle = 'dot', // 'pin' | 'dot' - pin shows teardrop marker, dot shows circle (legend should match)
  // Zoom & pan (opt-in)
  enableZoomPan = false,
  minZoom = 1,
  maxZoom = 8,
  initialZoom = 1,
  showZoomControls = false,
  // Optional CSS overrides for the zoom-controls container. Lets callers
  // reposition the controls (e.g. out of the way of other overlays) or
  // hide the slider/background without subclassing the component.
  zoomControlsStyle = null,
  // Allow panning past edges by a fraction of the visible viewport (0..0.5)
  panPaddingFraction = 0.15,
  panPaddingFractionX,
  panPaddingFractionY,
  panPaddingScaleWithZoom = false,
  /** When set with `onPhotoPaintReady`, fires once per key after the visible SVG image loads (or errors). */
  photoPaintReadyKey = null,
  onPhotoPaintReady = null,
}) => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const paintReadyEmittedRef = useRef(null);
  const [metrics, setMetrics] = useState(null);
  const [line, setLine] = useState(null);
  const [imgAspect, setImgAspect] = useState(null); // string like "W / H"
  // Natural image dimensions (world space)
  const [naturalSize, setNaturalSize] = useState({ w: 1000, h: 1000 });

  // Progressive image resolution. ``normalized`` is the raw tier map;
  // ``currentSrc`` is the best tier that has actually finished decoding
  // (thumb -> preview -> full). ``previewLoaded`` / ``fullLoaded``
  // drive the blur amount applied to the SVG <image>.
  const normalized = normalizeImageSource(imageUrl);
  const { currentSrc, previewLoaded, fullLoaded } =
    useProgressiveImage(imageUrl);
  // URL used for the hidden measurement <img>. Preview first (smaller
  // file = quicker aspect resolution), fall back to full or thumb when
  // a given tier is missing. Aspect ratio is identical across tiers, so
  // normalized coordinate math is unaffected by the swap.
  const measurementUrl =
    normalized.preview || normalized.full || normalized.thumb || '';
  // Full-res URL for zoom/pan reset keying. We only recenter the view
  // when a genuinely new asset is chosen, not when the progressive tier
  // flips mid-round.
  const fullUrl = normalized.full || measurementUrl;
  // Visible source for the SVG <image>. Fall back to measurementUrl so
  // the canvas never renders a blank frame.
  const displaySrc = currentSrc || measurementUrl;
  // Blur amount applied to the in-world SVG image while lower tiers are
  // showing. Measured in world-pixel space; the final on-screen blur
  // scales with the fit/zoom transform.
  const blurPx = fullLoaded ? 0 : previewLoaded ? 4 : 16;
  // Zoom / Pan state in normalized image space
  const [zoom, setZoom] = useState(() => Math.min(Math.max(initialZoom, minZoom), maxZoom));
  // Pan in world pixels (natural image coordinate space)
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(Math.min(Math.max(initialZoom, minZoom), maxZoom));
  const metricsRef = useRef(null);
  const isDraggingRef = useRef(false);
  const activePointerIdRef = useRef(null);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const didDragRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  // Compute aspect from intrinsic image and update metrics
  useEffect(() => {
    const update = () => {
      if (!containerRef.current || !imageRef.current) return;
      // If we don't yet have aspect from the intrinsic image, compute it
      const iw = imageRef.current.naturalWidth;
      const ih = imageRef.current.naturalHeight;
      if (iw > 0 && ih > 0) {
        const ratio = `${iw} / ${ih}`;
        if (ratio !== imgAspect) setImgAspect(ratio);
        setNaturalSize({ w: iw, h: ih });
      }
      const m = computeFitMetrics(containerRef.current, imageRef.current);
      setMetrics(m);
      metricsRef.current = m;
      // Recompute line if needed
      if (showLine && guess && answer && m) {
        setLine({
          x1: (guess.x || 0) * (imageRef.current.naturalWidth || 1000),
          y1: (guess.y || 0) * (imageRef.current.naturalHeight || 1000),
          x2: (answer.x || 0) * (imageRef.current.naturalWidth || 1000),
          y2: (answer.y || 0) * (imageRef.current.naturalHeight || 1000)
        });
      } else {
        setLine(null);
      }
    };
    const t = setTimeout(update, 20);
    window.addEventListener('resize', update);
    const img = imageRef.current;
    if (img) img.addEventListener('load', update);
    // Observe container size changes precisely
    let ro = null;
    if (containerRef.current && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => update());
      ro.observe(containerRef.current);
    }
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', update);
      if (img) img.removeEventListener('load', update);
      if (ro) {
        try { ro.disconnect(); } catch {}
      }
    };
  }, [measurementUrl, guess, answer, showLine, imgAspect]);

  useEffect(() => {
    paintReadyEmittedRef.current = null;
  }, [photoPaintReadyKey]);

  const notifyPhotoPaintReady = useCallback(() => {
    if (typeof onPhotoPaintReady !== 'function' || photoPaintReadyKey == null) {
      return;
    }
    if (paintReadyEmittedRef.current === photoPaintReadyKey) return;
    paintReadyEmittedRef.current = photoPaintReadyKey;
    onPhotoPaintReady();
  }, [photoPaintReadyKey, onPhotoPaintReady]);

  useEffect(() => {
    panRef.current = pan;
  }, [pan]);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  // Reset zoom/pan when the image changes (prevents carrying pan/zoom across rounds/maps).
  // Keyed on the *full* URL so the in-flight progressive swap from thumb
  // -> preview -> full does not accidentally recenter the view.
  useEffect(() => {
    const z = Math.min(Math.max(initialZoom, minZoom), maxZoom);
    setZoom(z);
    setPan({ x: 0, y: 0 });
    panRef.current = { x: 0, y: 0 };
    zoomRef.current = z;
  }, [fullUrl, initialZoom, minZoom, maxZoom]);

  // If zoom/pan gets disabled, reset to defaults so click-mapping stays predictable.
  useEffect(() => {
    if (enableZoomPan) return;
    const z = Math.min(Math.max(initialZoom, minZoom), maxZoom);
    setZoom(z);
    setPan({ x: 0, y: 0 });
    panRef.current = { x: 0, y: 0 };
    zoomRef.current = z;
  }, [enableZoomPan, initialZoom, minZoom, maxZoom]);

  const handleClick = (e) => {
    if (!onSelect) return;
    if (!containerRef.current || !imageRef.current || !metrics) return;
    if (enableZoomPan && didDragRef.current) {
      // Suppress click after drag
      didDragRef.current = false;
      return;
    }
    // Compute normalized using our fit metrics (robust to letterboxing)
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (clientX == null || clientY == null) return;
    const localX = clientX - rect.left - metrics.offsetX;
    const localY = clientY - rect.top - metrics.offsetY;
    const uX = Math.max(0, Math.min(1, localX / (metrics.imgWidth || 1)));
    const uY = Math.max(0, Math.min(1, localY / (metrics.imgHeight || 1)));
    let worldX = uX * (naturalSize.w / zoomRef.current) + panRef.current.x;
    let worldY = uY * (naturalSize.h / zoomRef.current) + panRef.current.y;
    // Clamp to image bounds
    worldX = Math.max(0, Math.min(naturalSize.w, worldX));
    worldY = Math.max(0, Math.min(naturalSize.h, worldY));
    const norm = { x: worldX / naturalSize.w, y: worldY / naturalSize.h };
    if (norm) onSelect(norm);
  };

  // Mouse wheel zooming (non-passive via addEventListener)
  const handleWheel = (e) => {
    const m = metricsRef.current;
    if (!enableZoomPan || !m || !containerRef.current) return;
    // Prevent page scroll while zooming
    e.preventDefault();
    const delta = e.deltaY;
    const rect = containerRef.current.getBoundingClientRect();
    const uX = (e.clientX - rect.left - m.offsetX) / (m.imgWidth || 1); // 0..1 over image box
    const uY = (e.clientY - rect.top - m.offsetY) / (m.imgHeight || 1);
    const cx = Math.max(0, Math.min(1, uX));
    const cy = Math.max(0, Math.min(1, uY));
    const currentZoom = zoomRef.current;
    // Zoom step factor
    const factor = Math.exp(-delta * 0.0015);
    let newZoom = Math.min(maxZoom, Math.max(minZoom, currentZoom * factor));
    if (newZoom === currentZoom) return;
    // Keep the point under cursor stable in world coords
    const worldX = panRef.current.x + cx * (naturalSize.w / currentZoom);
    const worldY = panRef.current.y + cy * (naturalSize.h / currentZoom);
    let newPanX = worldX - cx * (naturalSize.w / newZoom);
    let newPanY = worldY - cy * (naturalSize.h / newZoom);
    // Clamp pan so the viewport stays within [0,1] (+ padding)
    const { minX, maxX, minY, maxY } = getPanBoundsPx(
      newZoom,
      naturalSize.w,
      naturalSize.h,
      panPaddingFractionX ?? panPaddingFraction,
      panPaddingFractionY ?? panPaddingFraction,
      panPaddingScaleWithZoom
    );
    newPanX = Math.max(minX, Math.min(maxX, newPanX));
    newPanY = Math.max(minY, Math.min(maxY, newPanY));
    // If fully zoomed out, keep the view centered
    if (newZoom <= minZoom + 1e-6) {
      newPanX = 0;
      newPanY = 0;
    }
    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
    zoomRef.current = newZoom;
    panRef.current = { x: newPanX, y: newPanY };
  };

  // Attach non-passive wheel listener to avoid passive preventDefault errors
  useEffect(() => {
    if (!enableZoomPan) return;
    const el = containerRef.current;
    if (!el) return;
    const wheelHandler = (ev) => handleWheel(ev);
    el.addEventListener('wheel', wheelHandler, { passive: false });
    return () => {
      try { el.removeEventListener('wheel', wheelHandler, { passive: false }); } catch {}
    };
  // handleWheel captures refs and enableZoomPan; listener re-bound when enableZoomPan changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableZoomPan]);

  // Drag panning
  const beginDrag = (clientX, clientY, pointerId) => {
    isDraggingRef.current = true;
    activePointerIdRef.current = pointerId;
    setIsDragging(true);
    didDragRef.current = false;
    lastPosRef.current = { x: clientX, y: clientY };
  };
  const dragMove = (clientX, clientY) => {
    const m = metricsRef.current || metrics;
    if (!enableZoomPan || !isDraggingRef.current || !m) return;
    const dxPx = clientX - lastPosRef.current.x;
    const dyPx = clientY - lastPosRef.current.y;
    if (Math.abs(dxPx) > 2 || Math.abs(dyPx) > 2) didDragRef.current = true;
    lastPosRef.current = { x: clientX, y: clientY };

    const currentZoom = zoomRef.current;
    // Convert screen pixels into world pixels (natural image space).
    // IMPORTANT: do NOT multiply by zoom — zoom is already accounted for by (natW / currentZoom).
    const dWorldX = (dxPx / (m.imgWidth || 1)) * (naturalSize.w / currentZoom);
    const dWorldY = (dyPx / (m.imgHeight || 1)) * (naturalSize.h / currentZoom);

    const bounds = getPanBoundsPx(
      currentZoom,
      naturalSize.w,
      naturalSize.h,
      panPaddingFractionX ?? panPaddingFraction,
      panPaddingFractionY ?? panPaddingFraction,
      panPaddingScaleWithZoom
    );
    let nx = panRef.current.x - dWorldX;
    let ny = panRef.current.y - dWorldY;
    nx = Math.max(bounds.minX, Math.min(bounds.maxX, nx));
    ny = Math.max(bounds.minY, Math.min(bounds.maxY, ny));
    setPan({ x: nx, y: ny });
    panRef.current = { x: nx, y: ny };
  };
  const endDrag = () => {
    isDraggingRef.current = false;
    activePointerIdRef.current = null;
    setIsDragging(false);
  };

  const handlePointerDown = (e) => {
    if (!enableZoomPan) return;
    // Only drag-pan when zoomed in enough to need it.
    if ((zoomRef.current ?? zoom) <= 1) return;
    // Left-click / primary touch only
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // Avoid interfering with normal click-to-place on desktop.
    // For touch/pen, prevent default to avoid scroll/selection during pan.
    if (e.pointerType !== 'mouse') e.preventDefault();

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}

    beginDrag(e.clientX, e.clientY, e.pointerId);
  };
  const handlePointerMove = (e) => {
    if (!enableZoomPan) return;
    if (!isDraggingRef.current) return;
    if (activePointerIdRef.current != null && e.pointerId !== activePointerIdRef.current) return;
    if (e.pointerType !== 'mouse') e.preventDefault();
    dragMove(e.clientX, e.clientY);
  };
  const handlePointerUp = (e) => {
    if (activePointerIdRef.current != null && e.pointerId !== activePointerIdRef.current) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}
    endDrag();
  };
  const handlePointerCancel = (e) => {
    if (activePointerIdRef.current != null && e.pointerId !== activePointerIdRef.current) return;
    endDrag();
  };

  const transformProps = (() => {
    if (!metrics) return null;
    const baseSx = metrics.imgWidth / naturalSize.w;
    const baseSy = metrics.imgHeight / naturalSize.h;
    const tx = metrics.offsetX;
    const ty = metrics.offsetY;
    const panX = pan.x;
    const panY = pan.y;
    return { baseSx, baseSy, tx, ty, panX, panY, zoom };
  })();

  // When fully zoomed out, recenter the view (prevents "ending up in a corner" at zoom=1).
  useEffect(() => {
    const eps = 1e-6;
    if (!enableZoomPan) return;
    if (zoom > minZoom + eps) return;
    if (Math.abs(panRef.current.x) < eps && Math.abs(panRef.current.y) < eps) return;
    setPan({ x: 0, y: 0 });
    panRef.current = { x: 0, y: 0 };
  }, [enableZoomPan, zoom, minZoom]);

  return (
    <div
      className={`mcc-container ${className}`}
      ref={containerRef}
      onClick={handleClick}
      onPointerDown={enableZoomPan ? handlePointerDown : undefined}
      onPointerMove={enableZoomPan ? handlePointerMove : undefined}
      onPointerUp={enableZoomPan ? handlePointerUp : undefined}
      onPointerCancel={enableZoomPan ? handlePointerCancel : undefined}
      onDragStart={(e) => e.preventDefault()}
      onContextMenu={enableZoomPan ? (e) => e.preventDefault() : undefined}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: imgAspect || aspectRatio,
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
        userSelect: isDragging ? 'none' : 'auto',
        touchAction: enableZoomPan ? 'none' : 'auto',
        cursor: enableZoomPan ? (isDragging ? 'grabbing' : (zoom > 1 ? 'grab' : 'default')) : 'default',
        ...style
      }}
    >
      <img
        ref={imageRef}
        src={measurementUrl}
        alt="Map"
        className="mcc-image"
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'none', pointerEvents: 'none' }}
      />
      {/* Vector overlay bound to image box using transform */}
      {transformProps && (
        <svg
          className="mcc-overlay"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 5 }}
        >
          {/* IMPORTANT: transform order matters (SVG applies right-to-left).
              We want world->screen: (p - pan) * zoom, then scale to fit, then offset to account for letterboxing. */}
          <g transform={`translate(${transformProps.tx},${transformProps.ty}) scale(${transformProps.baseSx},${transformProps.baseSy}) scale(${transformProps.zoom}) translate(${-transformProps.panX},${-transformProps.panY})`}>
            {/* Map image rendered in the same transformed space to keep alignment.
                ``displaySrc`` is the best progressive tier that has decoded; the
                CSS blur softens the low-res placeholder while better tiers load. */}
            <image
              href={displaySrc}
              x="0"
              y="0"
              width={naturalSize.w}
              height={naturalSize.h}
              preserveAspectRatio="none"
              onLoad={notifyPhotoPaintReady}
              onError={notifyPhotoPaintReady}
              style={{
                filter: blurPx > 0 ? `blur(${blurPx}px)` : undefined,
                transition: 'filter 240ms ease-out',
              }}
            />
            {/* Line */}
            {showLine && line && (
              <line x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke="white" strokeWidth={2 / Math.max(transformProps.baseSx * transformProps.zoom, transformProps.baseSy * transformProps.zoom)} strokeDasharray="5,5" opacity="0.9" />
            )}
            {/* Multi-guess lines to answer */}
            {showLine && !line && answer && Array.isArray(guesses) && guesses.length > 0 && (() => {
              const list = guesses.filter(g => g && g.x != null && g.y != null);
              const strokeW = 2 / Math.max(transformProps.baseSx * transformProps.zoom, transformProps.baseSy * transformProps.zoom);
              const ax = (answer.x || 0) * naturalSize.w;
              const ay = (answer.y || 0) * naturalSize.h;
              return (
                <>
                  {list.map((g, i) => {
                    const stroke = g.color || pickColor(i);
                    return (
                      <line
                        key={`${g.username || i}-to-answer`}
                        x1={(g.x || 0) * naturalSize.w}
                        y1={(g.y || 0) * naturalSize.h}
                        x2={ax}
                        y2={ay}
                        stroke={stroke}
                        strokeWidth={strokeW}
                        strokeDasharray="5,5"
                        opacity="0.65"
                      />
                    );
                  })}
                </>
              );
            })()}
            {/* Guess markers (multiple) */}
            {Array.isArray(guesses) && (() => {
              const list = guesses.filter(g => g && g.x != null && g.y != null);
              const hi = highlightKey ? list.filter(g => g.username === highlightKey) : [];
              const lo = highlightKey ? list.filter(g => g.username !== highlightKey) : list;
              const others = (highlightKey ? lo : list).filter((g) => g.kind !== 'you');
              const yours = (highlightKey ? lo : list).filter((g) => g.kind === 'you');
              const ordered = highlightKey ? [...others, ...yours] : [...others, ...yours];
              const scale = 1 / Math.max(transformProps.baseSx * transformProps.zoom, transformProps.baseSy * transformProps.zoom);
              const pinPath = 'M0,0 L-6,-16 C-6,-22 0,-26 0,-26 S6,-22 6,-16 Z';
              const renderMarker = (g, i, highlighted = false) => {
                const isYou = g.kind === 'you';
                const fill = isYou ? (g.color || '#f43f5e') : (g.color || pickColor(i));
                const t = `translate(${g.x * naturalSize.w}, ${g.y * naturalSize.h}) scale(${scale})`;
                const label = g.label || (isYou ? 'You' : g.username) || '';
                const strokeW = isYou ? 2.25 : 1.5;
                return (
                  <g key={`${g.username || i}-${highlighted ? 'hi' : 'lo'}`} transform={t} style={{ zIndex: isYou ? 2 : 1 }}>
                    {highlighted && (markerStyle === 'pin' ? <path d={pinPath} fill="none" stroke={fill} strokeWidth={3} opacity="0.9" /> : <circle r={13} fill="none" stroke={fill} strokeWidth={3} opacity="0.9" />)}
                    {markerStyle === 'pin' ? (
                      <path d={pinPath} fill={fill} stroke="#fff" strokeWidth={strokeW} />
                    ) : (
                      <circle r={isYou ? 10 : 9} fill={fill} stroke="#fff" strokeWidth={strokeW} />
                    )}
                    {label ? (
                      <text
                        x={0}
                        y={12}
                        textAnchor="middle"
                        fontSize={10}
                        fontWeight={isYou ? 800 : 650}
                        fill="#fff"
                        stroke="#111"
                        strokeWidth={3}
                        paintOrder="stroke"
                        style={{ pointerEvents: 'none' }}
                      >
                        {label}
                      </text>
                    ) : null}
                  </g>
                );
              };
              return (
                <>
                  {ordered.map((g, i) => renderMarker(g, i, false))}
                  {hi.map((g, i) => renderMarker(g, i, true))}
                </>
              );
            })()}
            {/* Single guess fallback */}
            {!guesses?.length && guess && (() => {
              const t = `translate(${guess.x * naturalSize.w}, ${guess.y * naturalSize.h}) scale(${1 / (transformProps.baseSx * transformProps.zoom)}, ${1 / (transformProps.baseSy * transformProps.zoom)})`;
              const pinPath = 'M0,0 L-6,-16 C-6,-22 0,-26 0,-26 S6,-22 6,-16 Z';
              const fill = guess.kind === 'you' || !guess.kind ? (guess.color || '#f43f5e') : (guess.color || '#ef4444');
              const label = guess.label || (guess.kind === 'you' ? 'You' : guess.username) || 'You';
              return (
                <g transform={t}>
                  {markerStyle === 'pin' ? <path d={pinPath} fill={fill} stroke="#fff" strokeWidth={2.25} /> : <circle r={10} fill={fill} stroke="#fff" strokeWidth={2.25} />}
                  <text x={0} y={12} textAnchor="middle" fontSize={10} fontWeight={800} fill="#fff" stroke="#111" strokeWidth={3} paintOrder="stroke" style={{ pointerEvents: 'none' }}>{label}</text>
                </g>
              );
            })()}
            {/* Answer marker */}
            {answer && (
              <g transform={`translate(${answer.x * naturalSize.w}, ${answer.y * naturalSize.h}) scale(${1 / (transformProps.baseSx * transformProps.zoom)}, ${1 / (transformProps.baseSy * transformProps.zoom)})`}>
                <circle r={12} fill="#22c55e" stroke="#fff" strokeWidth={3} />
                <circle r={5} fill="#14532d" stroke="#bbf7d0" strokeWidth={1.5} />
                <text x={0} y={4} textAnchor="middle" fontSize={11} fill="#fff" fontWeight="bold">✓</text>
                <text x={0} y={22} textAnchor="middle" fontSize={10} fontWeight={800} fill="#bbf7d0" stroke="#111" strokeWidth={3} paintOrder="stroke" style={{ pointerEvents: 'none' }}>{answer.label || 'Answer'}</text>
              </g>
            )}
          </g>
        </svg>
      )}

      {enableZoomPan && showZoomControls && (
        <div
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            padding: '8px',
            background: 'rgba(0,0,0,0.4)',
            borderRadius: '12px',
            ...(zoomControlsStyle || {}),
          }}
          onWheel={handleWheel}
        >
          <button
            type="button"
            onClick={() => {
              const step = 1.2;
              const cx = 0.5;
              const cy = 0.5;
              if (!metrics) return;
              // emulate wheel around center
              const currentZoom = zoomRef.current;
              let newZoom = Math.min(maxZoom, Math.max(minZoom, currentZoom * step));
              if (newZoom === currentZoom) return;
              const worldX = panRef.current.x + cx * (naturalSize.w / currentZoom);
              const worldY = panRef.current.y + cy * (naturalSize.h / currentZoom);
              let newPanX = worldX - cx * (naturalSize.w / newZoom);
              let newPanY = worldY - cy * (naturalSize.h / newZoom);
              const b = getPanBoundsPx(
                newZoom,
                naturalSize.w,
                naturalSize.h,
                panPaddingFractionX ?? panPaddingFraction,
                panPaddingFractionY ?? panPaddingFraction,
                panPaddingScaleWithZoom
              );
              newPanX = Math.max(b.minX, Math.min(b.maxX, newPanX));
              newPanY = Math.max(b.minY, Math.min(b.maxY, newPanY));
              if (newZoom <= minZoom + 1e-6) {
                newPanX = 0;
                newPanY = 0;
              }
              setZoom(newZoom);
              setPan({ x: newPanX, y: newPanY });
              zoomRef.current = newZoom;
              panRef.current = { x: newPanX, y: newPanY };
            }}
            style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' }}
            aria-label="Zoom in"
          >+</button>
          <input
            type="range"
            min={minZoom}
            max={maxZoom}
            step="0.01"
            value={zoom}
            onChange={(e) => {
              const targetZoom = Math.min(maxZoom, Math.max(minZoom, parseFloat(e.target.value)));
              if (!metrics) return;
              // Zoom towards current view center
              const cx = 0.5;
              const cy = 0.5;
              const currentZoom = zoomRef.current;
              const worldX = panRef.current.x + cx * (naturalSize.w / currentZoom);
              const worldY = panRef.current.y + cy * (naturalSize.h / currentZoom);
              let newPanX = worldX - cx * (naturalSize.w / targetZoom);
              let newPanY = worldY - cy * (naturalSize.h / targetZoom);
              const b = getPanBoundsPx(
                targetZoom,
                naturalSize.w,
                naturalSize.h,
                panPaddingFractionX ?? panPaddingFraction,
                panPaddingFractionY ?? panPaddingFraction,
                panPaddingScaleWithZoom
              );
              newPanX = Math.max(b.minX, Math.min(b.maxX, newPanX));
              newPanY = Math.max(b.minY, Math.min(b.maxY, newPanY));
              if (targetZoom <= minZoom + 1e-6) {
                newPanX = 0;
                newPanY = 0;
              }
              setZoom(targetZoom);
              setPan({ x: newPanX, y: newPanY });
              zoomRef.current = targetZoom;
              panRef.current = { x: newPanX, y: newPanY };
            }}
            style={{ writingMode: 'vertical-lr', direction: 'rtl', height: 140 }}
            aria-label="Zoom"
          />
          <button
            type="button"
            onClick={() => {
              const step = 1/1.2;
              if (!metrics) return;
              const cx = 0.5;
              const cy = 0.5;
              const currentZoom = zoomRef.current;
              let newZoom = Math.min(maxZoom, Math.max(minZoom, currentZoom * step));
              const worldX = panRef.current.x + cx * (naturalSize.w / currentZoom);
              const worldY = panRef.current.y + cy * (naturalSize.h / currentZoom);
              let newPanX = worldX - cx * (naturalSize.w / newZoom);
              let newPanY = worldY - cy * (naturalSize.h / newZoom);
              const b = getPanBoundsPx(
                newZoom,
                naturalSize.w,
                naturalSize.h,
                panPaddingFractionX ?? panPaddingFraction,
                panPaddingFractionY ?? panPaddingFraction,
                panPaddingScaleWithZoom
              );
              newPanX = Math.max(b.minX, Math.min(b.maxX, newPanX));
              newPanY = Math.max(b.minY, Math.min(b.maxY, newPanY));
              if (newZoom <= minZoom + 1e-6) {
                newPanX = 0;
                newPanY = 0;
              }
              setZoom(newZoom);
              setPan({ x: newPanX, y: newPanY });
              zoomRef.current = newZoom;
              panRef.current = { x: newPanX, y: newPanY };
            }}
            style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' }}
            aria-label="Zoom out"
          >−</button>
        </div>
      )}

      {typeof renderExtra === 'function' ? renderExtra(metrics) : null}
    </div>
  );
};

// Compute displayed image box inside container using "contain" logic (no reliance on DOM rects)
function computeFitMetrics(containerEl, imageEl) {
  const cw = containerEl.clientWidth;
  const ch = containerEl.clientHeight;
  const iw = imageEl.naturalWidth || 1;
  const ih = imageEl.naturalHeight || 1;
  const r = iw / ih;
  const cr = cw / (ch || 1);
  let imgWidth, imgHeight, offsetX, offsetY;
  if (cr >= r) {
    // container wider than image ratio -> full height, pillarbox left/right
    imgHeight = ch;
    imgWidth = ch * r;
    offsetX = (cw - imgWidth) / 2;
    offsetY = 0;
  } else {
    // container taller than image ratio -> full width, letterbox top/bottom
    imgWidth = cw;
    imgHeight = cw / r;
    offsetX = 0;
    offsetY = (ch - imgHeight) / 2;
  }
  return { imgWidth, imgHeight, offsetX, offsetY, containerWidth: cw, containerHeight: ch };
}

export default MapCoordinateCanvas;

function pickColor(i) {
  const palette = ['#ef4444', '#3b82f6', '#a855f7', '#f59e0b', '#10b981', '#e11d48', '#6366f1'];
  return palette[i % palette.length];
}

// Compute allowed pan bounds for current zoom (world pixel units)
function getPanBoundsPx(z, natW, natH, padX = 0, padY = 0, scaleWithZoom = false) {
  const eps = 1e-6;
  const viewW = natW / z;
  const viewH = natH / z;
  const scale = scaleWithZoom ? Math.max(1, z) : 1;
  const marginW = Math.max(0, padX) * viewW * scale;
  const marginH = Math.max(0, padY) * viewH * scale;
  const minX = 0 - marginW - eps;
  const minY = 0 - marginH - eps;
  const maxX = (natW - viewW) + marginW + eps;
  const maxY = (natH - viewH) + marginH + eps;
  return { minX, maxX, minY, maxY };
}
